import { setRuntimeTokens } from "./runtimeTheme";
import { deriveThemeTokens } from "./deriveThemeTokens";
import { clearMapPinRasterCache } from "../utils/mapPinIcon";
import { clearMapDotPinCache } from "../utils/mapDotPin";

/**
 * Maps portal `tokens` JSON keys to CSS custom properties used in this repo.
 * See docs/theme-tokens-and-api.md
 */
export function applyThemeTokens(tokens) {
  if (!tokens || typeof tokens !== "object") return;
  const merged = deriveThemeTokens(tokens);
  setRuntimeTokens(merged);

  const root = document.documentElement;
  const set = (name, value) => {
    if (value == null || value === "") return;
    root.style.setProperty(name, String(value));
  };

  /* Proppins brand (index.css) */
  set("--proppins-gold", merged.primary);
  set("--proppins-gold-mid", merged.primary_mid);
  set("--proppins-gold-deep", merged.primary_deep);
  set("--proppins-gold-soft", merged.primary_soft_bg);
  set("--proppins-ink", merged.text_primary);
  set("--proppins-muted", merged.text_muted);
  set("--proppins-line", merged.line_brand);
  set("--proppins-gradient-primary", merged.gradient_primary);
  set("--proppins-gradient-horizontal", merged.gradient_horizontal);
  set("--proppins-gradient-ribbon", merged.gradient_ribbon);
  set("--proppins-shadow-gold", merged.shadow_gold);

  /* Bootstrap / site (site-background.css) */
  set("--bs-primary", merged.bs_primary ?? merged.primary);
  if (merged.bs_primary_rgb) {
    set("--bs-primary-rgb", String(merged.bs_primary_rgb).replace(/\s*,\s*/g, ", "));
  }
  set("--bs-primary-text-emphasis", merged.primary_emphasis_text);
  set("--bs-primary-bg-subtle", merged.primary_subtle_bg);
  set("--bs-primary-border-subtle", merged.primary_subtle_border);
  set("--bs-focus-ring-color", merged.primary_focus_ring);

  set("--site-bg-base", merged.surface_page);
  set("--site-bg-image", merged.site_bg_image);
  set("--cta-primary", merged.cta_primary);
  set("--cta-primary-hover", merged.cta_primary_hover);
  set("--cta-on-primary", merged.cta_on_primary);
  set("--proppins-panel-glass-bg", merged.panel_glass);
  set("--proppins-chip-bg", merged.chip_bg);
  set("--proppins-chip-border", merged.chip_border);
  set("--theme-text-secondary", merged.text_secondary);
  set("--theme-button-disabled-bg", merged.button_disabled_bg);

  /* Footer (:root defaults in site-background.css) */
  set("--footer-bg", merged.footer_bg);
  set("--footer-bg-soft", merged.footer_bg_soft);
  set("--footer-border", merged.footer_border);
  set("--footer-text", merged.footer_text);
  set("--footer-heading", merged.footer_heading);
  set("--footer-gold", merged.footer_accent ?? merged.primary);
  set("--footer-gold-hover", merged.footer_accent_hover);
  set("--footer-muted", merged.footer_muted);

  /* Location map shell (LocationMap2.css .locationmap-wrapper) */
  set("--lm-gold", merged.map_ui_gold);
  set("--lm-gold-deep", merged.map_ui_gold_deep);
  set("--lm-surface", merged.map_ui_surface);
  set("--lm-surface-2", merged.map_ui_surface_2);
  set("--lm-ring", merged.map_ui_ring);
  set("--lm-line", merged.line_subtle);
  set("--lm-ink", merged.text_ink_strong);
  set("--lm-ink-soft", merged.text_ink_soft);
  set("--lm-muted", merged.text_secondary);
  set("--lm-shadow-sm", merged.shadow_ink_sm);
  set("--lm-shadow-md", merged.shadow_ink_md);
  set("--lm-shadow-lg", merged.shadow_ink_lg);

  set("--map-background-color", merged.map_background_color);

  /* Map v2 (Archvi split layout) */
  set("--map-v2-shell-bg", merged.map_v2_shell_bg);
  set("--map-v2-canvas-bg", merged.map_v2_canvas_bg);
  set("--map-v2-panel-bg", merged.map_v2_panel_bg);
  set("--map-v2-panel-chip-bg", merged.map_v2_panel_chip_bg);
  set("--map-v2-row-bg", merged.map_v2_row_bg);
  set("--map-v2-row-text", merged.map_v2_row_text);
  set("--map-v2-row-muted", merged.map_v2_row_muted);
  set("--map-v2-pin-1", merged.map_v2_pin_1);
  set("--map-v2-pin-2", merged.map_v2_pin_2);
  set("--map-v2-pin-3", merged.map_v2_pin_3);
  set("--map-v2-pin-4", merged.map_v2_pin_4);
  set("--map-v2-selected", merged.primary);

  /* Global buttons (global-buttons.css) */
  set("--ui-border", merged.border_default);
  set("--ui-text", merged.ui_text);
  set("--ui-coral-gradient", merged.accent_warm_gradient);
  set("--ui-nav-frost", merged.nav_frost);
  set("--ui-nav-brand-wash", merged.nav_brand_wash);

  /* Semantic bridge: components should use var(--theme-*) / Tailwind `theme` colors — not raw hex */
  set("--theme-surface-white", merged.surface_white);
  set("--theme-surface-muted-panel", merged.surface_muted_panel);
  set("--theme-surface-image-placeholder", merged.surface_image_placeholder);
  set("--theme-neutral-200", merged.neutral_200);
  set("--theme-neutral-300", merged.neutral_300);
  set("--theme-neutral-400", merged.neutral_400);
  set("--theme-neutral-500", merged.neutral_500);
  set("--theme-neutral-600", merged.neutral_600);
  set("--theme-neutral-700", merged.neutral_700);
  set("--theme-border-neutral", merged.border_neutral);
  set("--theme-border-strong", merged.border_strong);
  set("--theme-accent-marketing-cyan", merged.accent_marketing_cyan);
  set("--theme-accent-marketing-orange", merged.accent_marketing_orange);
  set("--theme-gradient-marketing-horizontal", merged.gradient_marketing_horizontal);
  set("--theme-auth-panel-gradient", merged.auth_panel_gradient);
  set("--theme-overlay-image-bottom", merged.overlay_image_bottom);
  set("--theme-oauth-facebook", merged.oauth_facebook);
  set("--theme-info-surface", merged.info_surface);
  set("--theme-info-ink", merged.info_ink);
  set("--theme-info-surface-hover", merged.info_surface_hover);
  set("--theme-teal-deep", merged.teal_deep);
  set("--theme-accent-teal-700", merged.accent_teal_700);
  set("--theme-auth-panel-dark-1", merged.auth_panel_dark_1);
  set("--theme-auth-panel-dark-2", merged.auth_panel_dark_2);
  set("--theme-auth-panel-dark-3", merged.auth_panel_dark_3);
  set("--theme-text-cream", merged.theme_text_cream);
  set("--theme-line-extralight", merged.line_extralight);
  set("--theme-line-light-gray", merged.line_light_gray);
  set("--theme-surface-gray-soft", merged.surface_gray_soft);
  set("--theme-border-gray-mid", merged.border_gray_mid);
  set("--theme-surface-warm-gray", merged.surface_warm_gray);
  set("--theme-text-gray-mid", merged.text_gray_mid);
  set("--theme-state-error-bg", merged.state_error_bg);
  set("--theme-state-error-ink", merged.state_error_ink);
  set("--text-ink-strong", merged.text_ink_strong);
  set("--primary-emphasis-text", merged.primary_emphasis_text);
  set("--pin-ring-bronze", merged.pin_ring_bronze);
  set("--theme-slate-50", merged.slate_50);
  set("--theme-slate-100", merged.slate_100);
  set("--theme-slate-200", merged.slate_200);
  set("--theme-slate-300-ui", merged.slate_300_ui);
  set("--theme-slate-600", merged.slate_600);
  set("--theme-slate-700", merged.slate_700);
  set("--theme-slate-750", merged.slate_750);
  set("--theme-slate-800", merged.slate_800);
  set("--theme-cool-blue-tint", merged.cool_blue_tint);
  set("--theme-surface-gray-f1", merged.surface_gray_f1);
  set("--theme-text-ui-555", merged.text_ui_555);
  set("--theme-state-error-bright", merged.state_error_bright);
  set("--theme-gradient-state-success", merged.gradient_state_success);
  set("--theme-gradient-state-error", merged.gradient_state_error);
  set("--theme-gradient-state-error-soft", merged.gradient_state_error_soft);
  set("--theme-state-error-border-soft", merged.state_error_border_soft);
  set("--theme-state-success-from", merged.state_success_from);
  set("--theme-state-success-to", merged.state_success_to);
  set("--theme-state-error-from", merged.state_error_from);
  set("--theme-state-error-to", merged.state_error_to);
  set("--theme-state-error-tint-1", merged.state_error_tint_1);
  set("--theme-state-error-tint-2", merged.state_error_tint_2);
  set("--theme-toast-shadow-success", merged.toast_shadow_success);
  set("--theme-toast-shadow-error", merged.toast_shadow_error);
  set("--theme-services-card-bg", merged.services_card_bg);
  set("--theme-services-cta-bg", merged.services_cta_bg);
  set("--theme-services-cta-hover", merged.services_cta_hover);

  clearMapPinRasterCache();
  clearMapDotPinCache();
}
